import { Injectable } from '@angular/core';
import { PythonHelper } from '../helpers/python.helper';


@Injectable({
  providedIn: 'root',
})
export class PythonRuntimeService {
  private pythonRuntime: any;
  private jsExternalModulesNamespaces: any;
  private isInitialized: boolean = false;

  constructor() { }

  async initialize(runtime: any, jsExternalModulesNamespaces: any) {
    this.pythonRuntime = runtime;
    this.jsExternalModulesNamespaces = jsExternalModulesNamespaces;
    // We'll now use the module system's initialization
    this.isInitialized = true;
  }

  async executeCode(code: string, namespaceObj?: any, contextId?: string): Promise<any> {
    if (!this.isInitialized) {
      throw new Error('Runtime not initialized');
    }

    const executionCode = `await module_system.execute_code(
      ${JSON.stringify(code)},
      ${namespaceObj ? JSON.stringify(namespaceObj) : 'None'},
      ${contextId ? JSON.stringify(contextId) : 'None'}
    )`;

    let result: any;
    try {
      result = await this.pythonRuntime.runPythonAsync(executionCode);
    } catch (error: any) {
      const systemExitResult = this.handleSystemExitError(error);
      if (systemExitResult) {
        return systemExitResult;
      }
      throw error;
    }

    // Extract fields individually — do NOT convert the whole dict.
    // `namespace` contains all step variables (numpy arrays, PIL images, etc.)
    // and bulk-converting it via pythonToJs triggers _python2js_buffer_recursive
    // which OOMs on large arrays.
    let output = '';
    let error = '';
    let exports: any[] = [];
    let lastExpressionResult: any = undefined;

    try {
      if (result && typeof result.get === 'function') {
        output = result.get('output') ?? '';
        error = result.get('error') ?? '';
        const rawExports = result.get('exports');
        exports = rawExports ? PythonHelper.pythonToJs(rawExports, this.pythonRuntime) ?? [] : [];
        const rawLast = result.get('last_expression_result');
        if (rawLast !== undefined && rawLast !== null) {
          lastExpressionResult = PythonHelper.pythonToJs(rawLast, this.pythonRuntime);
        }
      } else if (result) {
        ({ output = '', error = '', exports = [], last_expression_result: lastExpressionResult = undefined }
          = PythonHelper.pythonToJs(result, this.pythonRuntime));
      }
    } finally {
      if (result && typeof result.destroy === 'function') {
        result.destroy();
      }
    }

    output = typeof output === 'string' ? output.trim() : '';
    error = typeof error === 'string' ? error.trim() : '';

    return {
      output: `${output} ${error}`,
      namespace: {},
      exports,
      error: error || null,
      lastExpressionResult
    };
  }

  getPythonRuntime() {
    return this.pythonRuntime;
  }

  /**
   * Pyodide propagates SystemExit (from sys.exit) as an uncaught PythonError.
   * exit code 0 is normal termination; non-zero codes are failures.
   */
  private handleSystemExitError(error: any): {
    output: string;
    namespace: Record<string, never>;
    exports: never[];
    error: string | null;
    lastExpressionResult: undefined;
  } | null {
    const message = error?.message ?? String(error);
    const match = message.match(/SystemExit:\s*(\S+)/);
    if (!match) {
      return null;
    }

    const rawCode = match[1];
    const exitCode = rawCode === 'None' ? 0 : parseInt(rawCode, 10);
    const normalizedCode = Number.isNaN(exitCode) ? 0 : exitCode;

    if (normalizedCode === 0) {
      return {
        output: 'Code executed successfully',
        namespace: {},
        exports: [],
        error: null,
        lastExpressionResult: undefined,
      };
    }

    return {
      output: '',
      namespace: {},
      exports: [],
      error: `Execution terminated with exit code ${normalizedCode}`,
      lastExpressionResult: undefined,
    };
  }
}
